import './styles.scss'
import UnbeatableSection from '../../Components/UnbeatableSection'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { solid, brands, regular } from '@fortawesome/fontawesome-svg-core/import.macro'
import useInView, { animClass } from '../../Hooks/useInView'
import Input from '../../Components/Input'
import InputArea from '../../Components/InputArea'
import Select from '../../Components/Select'
import CustomButton from '../../Components/CustomButton'
import { useForm } from '../../Hooks/useForm'
import emailjs from '@emailjs/browser'
import { useState } from 'react'

const templateId = process.env.REACT_APP_EMAILJS_CONTACT_TEMPLATE
const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID
const publicId = process.env.REACT_APP_EMAILJS_PUBLIC_KEY

const SERVICE_OPTIONS = [
  { value: 'air-freight', label: 'Air Freight' },
  { value: 'ocean-freight', label: 'Ocean Freight' },
  { value: 'cargo-transportation', label: 'Cargo Transportation' },
  { value: 'packaging-storage', label: 'Packaging & Storage' },
  { value: 'general', label: 'General Inquiry' },
]

const validate = (form) => {
  const errs = {}
  if (!form.name.trim()) errs.name = 'Full name is required'
  if (!form.email.trim()) errs.email = 'Email address is required'
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
    errs.email = 'Enter a valid email address'
  if (!form.subject.trim()) errs.subject = 'Subject is required'
  if (!form.msg.trim()) errs.msg = 'Message is required'
  return errs
}

const ContactUs = () => {
  const [infoRef, infoInView] = useInView()
  const [formRef, formInView] = useInView()
  const [submitted, setSubmitted] = useState(false)
  const [errors, setErrors] = useState({})
  const [submitError, setSubmitError] = useState(false)

  const { form, reset, onChange } = useForm({
    name: '',
    company: '',
    email: '',
    phone: '',
    service: '',
    subject: '',
    msg: '',
    loading: false,
  })

  const handleChange = (e) => {
    onChange(e.target.name, e.target.value)
    if (errors[e.target.name]) {
      setErrors((prev) => ({ ...prev, [e.target.name]: undefined }))
    }
  }

  const isFormValid = Boolean(
    form.name.trim() && form.email.trim() && form.subject.trim() && form.msg.trim()
  )

  const handleSubmit = async (e) => {
    e.preventDefault()

    const errs = validate(form)
    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      return
    }

    onChange('loading', true)
    setSubmitError(false)

    try {
      await emailjs.send(serviceId, templateId, form, publicId)
      reset()
      setErrors({})
      setSubmitted(true)
    } catch (error) {
      setSubmitError(true)
    }

    onChange('loading', false)
  }

  return (
    <div className="contact-us">
      <div className="contact-us__top">
        <h1>Contact Us</h1>
        <p>We're here to help. Reach out and we'll respond as soon as possible.</p>
      </div>

      <div className="contact-us__content">
        <section className="contact-us__content__main">

          <div ref={infoRef} className={`contact-info ${animClass(infoInView, 'fadeInLeft')}`}>
            <h2>Get In Touch</h2>
            <p>
              Dove Global Logistics is available to meet your shipping and
              logistics needs. Call or email us anytime for a free quote.
            </p>

            <div className="info-card">
              <div className="info-card__icon">
                <FontAwesomeIcon icon={solid('phone')} />
              </div>
              <div className="info-card__body">
                <span className="info-card__label">Call Us</span>
                <span className="info-card__value">+1 (469) 300-3088</span>
              </div>
            </div>

            <div className="info-card">
              <div className="info-card__icon">
                <FontAwesomeIcon icon={regular('envelope')} />
              </div>
              <div className="info-card__body">
                <span className="info-card__label">Email Us</span>
                <span className="info-card__value">business@doveglobal.us</span>
              </div>
            </div>

            <div className="info-card">
              <div className="info-card__icon">
                <FontAwesomeIcon icon={solid('location-dot')} />
              </div>
              <div className="info-card__body">
                <span className="info-card__label">Our Office</span>
                <span className="info-card__value">Global Facility Office</span>
              </div>
            </div>

            <div className="info-card">
              <div className="info-card__icon">
                <FontAwesomeIcon icon={solid('clock')} />
              </div>
              <div className="info-card__body">
                <span className="info-card__label">Business Hours</span>
                <span className="info-card__value">Mon – Fri: 8:00am – 6:00pm</span>
                <span className="info-card__value">Sat: 9:00am – 2:00pm</span>
              </div>
            </div>

            <div className="socials">
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a href="#" target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={brands('facebook-f')} />
              </a>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a href="#" target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={brands('linkedin-in')} />
              </a>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a href="#" target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={brands('twitter')} />
              </a>
            </div>
          </div>

          <div ref={formRef} className={`contact-form ${animClass(formInView, 'fadeInRight')}`}>
            <h2>Send Us a Message</h2>
            <p>Fill out the form below and we'll get back to you shortly.</p>

            {submitted ? (
              <div className="contact-form__success">
                <FontAwesomeIcon className="success-icon" icon={solid('circle-check')} />
                <h3>Message Sent!</h3>
                <p>Thank you for reaching out. We'll be in touch soon.</p>
                <button
                  className="send-again"
                  onClick={() => {
                    setSubmitted(false)
                    setSubmitError(false)
                    setErrors({})
                  }}
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {submitError && (
                  <div className="contact-form__error-banner">
                    <FontAwesomeIcon icon={solid('circle-exclamation')} />
                    Something went wrong. Please try again or email us directly at{' '}
                    <strong>business@doveglobal.us</strong>
                  </div>
                )}

                <div className="form-row">
                  <Input
                    title="Full Name"
                    required
                    placeholder="John Doe"
                    value={form.name}
                    onChange={handleChange}
                    name="name"
                    error={errors.name}
                  />
                  <Input
                    title="Company Name"
                    placeholder="Acme Corp"
                    value={form.company}
                    onChange={handleChange}
                    name="company"
                  />
                </div>

                <div className="form-row">
                  <Input
                    title="Email Address"
                    type="email"
                    required
                    placeholder="john@example.com"
                    value={form.email}
                    onChange={handleChange}
                    name="email"
                    error={errors.email}
                  />
                  <Input
                    title="Phone Number"
                    placeholder="+1 (555) 000-0000"
                    value={form.phone}
                    onChange={handleChange}
                    name="phone"
                  />
                </div>

                <div className="form-row">
                  <Select
                    title="Service of Interest"
                    placeholder="Select a service..."
                    value={form.service}
                    onChange={handleChange}
                    name="service"
                    options={SERVICE_OPTIONS}
                  />
                  <Input
                    title="Subject"
                    required
                    placeholder="How can we help?"
                    value={form.subject}
                    onChange={handleChange}
                    name="subject"
                    error={errors.subject}
                  />
                </div>

                <InputArea
                  title="Message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="Tell us more about your inquiry..."
                  value={form.msg}
                  onChange={handleChange}
                  name="msg"
                  error={errors.msg}
                />

                <div className="form-footer">
                  <div className="form-footer__meta">
                    <FontAwesomeIcon icon={solid('clock')} />
                    <span>We typically respond within 1 business day</span>
                  </div>
                  <div className="form-footer__privacy">
                    <FontAwesomeIcon icon={solid('lock')} />
                    <span>Your information is never shared with third parties.</span>
                  </div>
                </div>

                <CustomButton
                  loading={form.loading}
                  disabled={!isFormValid || form.loading}
                  label="Send Message"
                  otherClassName="submit-btn"
                />
              </form>
            )}
          </div>

        </section>

      </div>

      <UnbeatableSection />
    </div>
  )
}

export default ContactUs
